import * as logger from "../utils/logger";
import Api from "../services/api";
import constants from "../constants";
import { IApiError, TMultiCallParam } from "../utils/api.interfaces";
import errorFilter from "../utils/error.filter";
import { isNetworkError, isOverLimitException } from "../utils/errorHandler/error.types";
import { getAllLogsAndKeysFromDb, getLogsByTypesAndSources, clearAllUploadedKeysFromDb, trimLogsInDb, drainFallbackLogs, restoreFallbackLogs } from "../utils/indexedDbLogger";
import { isVerboseLogging } from "../utils/logger.internal";
import { createTimeoutSignal } from "./common";
import { cleanSessionAndPassword } from "../utils/helpers";
import PerformanceProfilerFactory from "../utils/performanceProfiler/performanceProfilerFactory";
import { MeasureName } from "../utils/performanceProfiler/measureName";

export const MAX_VALUE_SIZE = 1024 * 1024 * 4;
export const TRUNC_STRING = "{TRUNC} ";
const MAX_ALLOWED_VALUE_LENGTH = MAX_VALUE_SIZE - TRUNC_STRING.length;

export function getUploadChunkSize(baseChunkSize: number = constants.LOGGING.DEFAULT_UPLOAD_CHUNK_SIZE): number {
    return isVerboseLogging() ? baseChunkSize * 2 : baseChunkSize;
}

let currentUpload: Promise<boolean> | undefined = undefined;

// For testing purposes - resets the upload state
export function resetUploadState() {
    currentUpload = undefined;
}

export async function uploadEventLogsChunk(api: Api, logs: logger.ILog[], timeoutPromise: Promise<void> | undefined): Promise<[logger.ILog[], logger.ILog[], IApiError?]> {
    if (logs.length === 0) {
        return [[], []];
    }
    let traceTextCalls: TMultiCallParam[] = logs.map(eventLog => {
        let formattedLog = {
            traceTag: `drive-${ eventLog.type }`.toLowerCase(),
            // Third line of defense (MYG-142393): sanitize credentials in the serialized payload
            // before it leaves the device. Layer 1 = password input guard in handleDriveAppInteraction;
            // Layer 2 = cleanSessionAndPassword in logger.internal sanitizeMessage;
            // Layer 3 = this call, applied to the final JSON string at upload time.
            value: cleanSessionAndPassword(JSON.stringify(eventLog))
        };

        if (formattedLog.value.length > MAX_VALUE_SIZE) {
            formattedLog.value = `${ TRUNC_STRING }${ formattedLog.value.substring(0, MAX_ALLOWED_VALUE_LENGTH) }`;
        }
        return ["TraceText", formattedLog];
    });

    try {
        timeoutPromise
            ? await Promise.race([api.multiCallPromise(traceTextCalls), timeoutPromise])
            : await api.multiCallPromise(traceTextCalls);
        return [logs, []];
    } catch (error) {
        if (error.message.includes("timed out")) {
            logger.error(logger.Source.SYNC, error.message);
            throw error;
        }
        let requestIndex = error?.data?.requestIndex || 0;
        const successLogs = logs.slice(0, Math.max(requestIndex, 0));
        const unsentLogs = logs.slice(Math.max(requestIndex, 0));

        return [successLogs, unsentLogs, error];
    }
}

async function getFilteredLogs(chunkSize: number) {
    if (isVerboseLogging()) {
        const { logs: uploadChunk, keys: uploadChunkKeys } = await getAllLogsAndKeysFromDb(chunkSize);
        return { uploadChunk, uploadChunkKeys };
    } else {
        const { logs: uploadChunk, keys: uploadChunkKeys } = await getLogsByTypesAndSources(chunkSize);

        return { uploadChunk, uploadChunkKeys };
    }
}

/**
 * Drains the in-memory fallback buffer, clamps to `budget`, and uploads the clamped batch.
 * Any logs beyond `budget` are immediately re-queued so they drain in the next cycle.
 */
async function uploadFallbackBatch(
    api: Api,
    budget: number,
    timeoutPromise: Promise<void> | undefined
): Promise<{ totalSent: number; success: logger.ILog[]; failed: logger.ILog[]; hasFailure: boolean; lastError: IApiError | null; wasCapReached: boolean }> {
    const drained = drainFallbackLogs();
    const toSend = drained.slice(0, budget);
    const excess = drained.slice(budget);
    if (excess.length > 0) {
        restoreFallbackLogs(excess);
    }
    if (toSend.length === 0) {
        return { totalSent: 0, success: [], failed: [], hasFailure: false, lastError: null, wasCapReached: excess.length > 0 };
    }
    try {
        const [fbSuccess, fbFailed, fbError] = await uploadEventLogsChunk(api, toSend, timeoutPromise);
        if (fbFailed.length > 0) {
            restoreFallbackLogs(fbFailed);
        }
        const hasFailure = !!fbError && (errorFilter.isIgnored(fbError) || isNetworkError(fbError));
        return {
            totalSent: toSend.length,
            success: fbSuccess,
            failed: fbFailed,
            hasFailure,
            lastError: fbError ?? null,
            wasCapReached: excess.length > 0
        };
    } catch (e: any) {
        restoreFallbackLogs(toSend);
        logger.error(logger.Source.SYNC, `An unexpected error occurred during fallback log upload: ${ e.message }`);
        return { totalSent: 0, success: [], failed: toSend, hasFailure: true, lastError: e as IApiError, wasCapReached: excess.length > 0 };
    }
}

/**
 * Uploads event logs from IndexedDB to the server in chunks.
 *
 * @param api - The API instance to use for uploading.
 * @param chunkSize - Number of logs to upload per iteration. Defaults to `constants.LOGGING.DEFAULT_UPLOAD_CHUNK_SIZE`.
 * @param maxUploadLoops - Maximum number of upload iterations to perform.
 *   Pass `1` for a single-shot upload, `2` to allow up to two iterations, etc.
 *   Pass `-1` to upload continuously until all logs are sent or an error occurs.
 *   Defaults to `constants.LOGGING.DEFAULT_MAX_UPLOAD_LOOPS`.
 * @param timeout - Timeout in milliseconds after which the upload will abort. Defaults to `constants.LOGGING.DEFAULT_UPLOAD_TIMEOUT`.
 * @param maxLogsPerCycle - Hard ceiling on the total number of logs (TraceText calls) sent in this invocation,
 *   regardless of `maxUploadLoops`. Prevents runaway log queues from generating unbounded server traffic.
 *   Defaults to `constants.LOGGING.MAX_LOGS_PER_UPLOAD_CYCLE`.
 */
export async function uploadEventLogsFromIndexedDb(
    api: Api,
    chunkSize = constants.LOGGING.DEFAULT_UPLOAD_CHUNK_SIZE,
    maxUploadLoops = constants.LOGGING.DEFAULT_MAX_UPLOAD_LOOPS,
    timeout = constants.LOGGING.DEFAULT_UPLOAD_TIMEOUT,
    maxLogsPerCycle = constants.LOGGING.MAX_LOGS_PER_UPLOAD_CYCLE
): Promise<boolean> {
    // If an upload is already in progress, wait for it to complete instead of skipping
    if (currentUpload) {
        logger.log(logger.Source.SYNC, "An EventLogs upload is already in progress. Waiting for it to complete...");
        return currentUpload;
    }

    // Create a new promise for this upload that others can wait on
    let resolveUpload: ((result: boolean) => void) = () => {};
    currentUpload = new Promise<boolean>(resolve => {
        resolveUpload = resolve;
    });

    // Safe default: assume failure until proven otherwise
    let result = false;

    try {
        logger.log(logger.Source.SYNC, "Starting indexedDb EventLogs upload");

        // These performance measurements help track the duration of the entire upload process, but might need to be uploaded next time if we're in the process of logging out.
        const eventLogsUploadProfiler = PerformanceProfilerFactory.getInstance(MeasureName.UploadEventLogs);
        eventLogsUploadProfiler.start();

        let timeoutPromise: Promise<void> | undefined = undefined;
        if (timeout) {
            timeoutPromise = createTimeoutSignal(`uploadEventLogsFromIndexedDb timed out after ${ timeout } milliseconds`, timeout) as unknown as Promise<void>;
        }

        // Initialize trackers for successful, failed logs, and errors.
        const successLogs: logger.ILog[] = [];
        const failedLogs: logger.ILog[] = [];
        let hasUploadFailure = false;
        let lastError: IApiError | null = null;
        let uploadLoopCount = 0;
        // Tracks the total number of TraceText calls submitted this invocation (success or partial
        // failure) so we can enforce maxLogsPerCycle regardless of the upload outcome.
        let totalLogsThisCycle = 0;
        let wasCapReached = false;

        // Send any in-memory fallback logs before reading IDB, so IDB failures later
        // in the same upload do not prevent them from reaching the server.
        // Clamp the batch to the per-cycle budget so the fallback queue never bypasses the rate cap.
        const fallbackBudget = Math.max(0, maxLogsPerCycle - totalLogsThisCycle);
        const fb = await uploadFallbackBatch(api, fallbackBudget, timeoutPromise);
        totalLogsThisCycle += fb.totalSent;
        successLogs.push(...fb.success);
        failedLogs.push(...fb.failed);
        hasUploadFailure = fb.hasFailure;
        lastError = fb.lastError;
        wasCapReached = fb.wasCapReached;

        while (!hasUploadFailure) {
            // Enforce the per-cycle rate cap before reading the next chunk from IDB.
            const remaining = maxLogsPerCycle - totalLogsThisCycle;
            if (remaining <= 0) {
                wasCapReached = true;
                logger.log(logger.Source.SYNC, `IndexedDb EventLogs: Rate cap of ${ maxLogsPerCycle } logs per cycle reached. Stopping; any unprocessed logs will upload in the next cycle.`);
                break;
            }

            // Clamp the chunk size so we never fetch more logs than the cap allows.
            const effectiveChunkSize = Math.min(chunkSize, remaining);
            const { uploadChunk, uploadChunkKeys } = await getFilteredLogs(effectiveChunkSize);

            // If no logs are found, we are done.
            if (uploadChunk.length === 0) {
                break;
            }

            try {
                // Await the result of uploading the current chunk.
                const [uploadSuccessLogs, uploadFailedLogs, error] = await uploadEventLogsChunk(api, uploadChunk, timeoutPromise);

                successLogs.push(...uploadSuccessLogs);
                failedLogs.push(...uploadFailedLogs);
                // Count all submitted calls, not just successes, so the budget accurately reflects
                // what the server received — partial-failure retries consume budget too.
                totalLogsThisCycle += uploadChunk.length;

                // If the chunk was uploaded successfully, remove it from storage.
                if (uploadSuccessLogs.length > 0) {
                    // Get the keys corresponding to the successful logs.
                    const successKeys = uploadChunkKeys.slice(0, uploadSuccessLogs.length);
                    await clearAllUploadedKeysFromDb(successKeys);
                }

                // Record the last error if one occurred.
                if (error) {
                    lastError = error;
                    if (isOverLimitException(error)) {
                        // Server is throttling TraceText; stop this invocation. The next scheduled
                        // upload cycle acts as the natural cooldown.
                        logger.log(logger.Source.SYNC, "IndexedDb EventLogs: Server returned OverLimit for TraceText. Stopping this invocation.");
                        // Defensive: stop the loop explicitly so a future removal of OverLimitException
                        // from errorFilter.isIgnored cannot silently restore retry-storm behavior.
                        hasUploadFailure = true;
                    }
                    // If the error is a network or ignored error, set a flag to stop subsequent uploads.
                    if (errorFilter.isIgnored(error) || isNetworkError(error)) {
                        hasUploadFailure = true;
                    }
                }
            } catch (e: any) {
                // Handle unexpected errors from the upload function itself.
                lastError = e as IApiError;
                hasUploadFailure = true;
                failedLogs.push(...uploadChunk);
                logger.error(logger.Source.SYNC, `An unexpected error occurred during log upload: ${ e.message }`);
            }
            uploadLoopCount++;
            if (maxUploadLoops !== -1 && uploadLoopCount >= maxUploadLoops) {
                // Stop once the maximum number of upload iterations has been reached.
                break;
            }
        }

        eventLogsUploadProfiler.end();

        // After processing all chunks, log the final results.
        if (successLogs.length > 0) {
            logger.log(logger.Source.SYNC, `IndexedDb EventLogs: Uploaded ${ successLogs.length } logs.`);
        }

        if (failedLogs.length > 0) {
            const errorMessage = `IndexedDb EventLogs: Failed to upload ${ failedLogs.length } logs.${ (lastError ? ` Last error: ${ lastError.message }` : "") }`;
            logger.error(logger.Source.SYNC, errorMessage);
        }
        let trimmedLogsCount = await trimLogsInDb(0, api.getUser());
        trimmedLogsCount && logger.log(logger.Source.SYNC, `IndexedDb EventLogs: Trimmed ${ trimmedLogsCount } logs.`);

        // Return false if any upload failed OR if the rate cap was hit (queue was not fully drained).
        result = failedLogs.length === 0 && !wasCapReached;
        return result;
    } finally {
        // Always mark this upload as complete and clear the reference, even if an error occurred
        resolveUpload(result);
        currentUpload = undefined;
    }
}
